import React, {useEffect, useReducer, useRef} from 'react';
import {Platform, StyleProp, StyleSheet, View, ViewStyle} from 'react-native';

import TextInput from '../../themed/TextInput';
import {fontFamilyStyle} from '../Text';

type State = {cursor: number; values: string[]};

type Action =
  | {type: 'reset'}
  | {
      type: 'insertText';
      payload: {
        value: string;
        index: number;
        onChangeText: any;
      };
    }
  | {
      type: 'insertFromPaste';
      payload: {
        value: string;
        onChangeText: any;
      };
    }
  | {
      type: 'sms';
      payload: {
        value: string;
        onChangeText: any;
      };
    }
  | {
      type: 'deleteContentBackward';
      payload: {
        index: number;
        onChangeText: any;
      };
    };

function reducer(state: State, action: Action) {
  if (action.type === 'reset') {
    return {cursor: 0, values: Array(state.values.length).fill('')};
  } else if (action.type === 'insertText') {
    state.values[action.payload.index] = action.payload.value;
  } else if (action.type === 'insertFromPaste') {
    for (var i = 0; i < state.values.length; i++) {
      state.values[i] = action.payload.value[i];
    }
  } else if (action.type === 'deleteContentBackward') {
    state.values[action.payload.index] = '';
  }
  // code from sms safari ios is sent as several
  // onChange event nativeEvent.type = 'input'
  // chrome ios sms sends as 1 value so can use the simpler insertFromPaste
  else if (action.type === 'sms') {
    // value was sometimes 2 char. just the last char should be used.
    state.values[state.cursor] =
      action.payload.value[action.payload.value.length - 1];
    if (state.cursor === state.values.length - 1) {
      state.cursor = 0;
    } else {
      state.cursor = state.cursor + 1;
    }
  }

  // side effect
  action.payload.onChangeText?.(state.values.join(''));

  return {cursor: state.cursor, values: [...state.values]};
}

export type OtpProps = {
  numInputs: number;
  onChangeText?: (text: string) => void;
  containerStyle?: StyleProp<ViewStyle>;
  onEnter?: () => void;
  onTouchStart?: (e: any) => void;
  onBlur?: (e: any) => void;

  /**
   * Should apply error styles
   */
  error?: boolean;

  /**
   * This is just used to reset the field when set to empty string
   */
  value?: any;
};

const styles = StyleSheet.create({
  containerStyle: {
    flexDirection: 'row',
    gap: 8,
  },
  textInputStyle: {
    ...fontFamilyStyle,
    textAlign: 'center',
    width: 44,
    paddingHorizontal: 10,
  },
  textInputStyleError: {
    color: '#C81E1E',
  },
  errorStyle: {
    display: 'none',
  },
});

/**
 * Otp for rewards program. evaluated other but they were a bit buggy or didn't have the behaviour we required
 * react-native-otp-textinput react-otp-input
 * @param param0
 * @returns
 */
export default function Otp({
  numInputs = 4,
  onChangeText,
  containerStyle,
  onEnter,
  onTouchStart,
  onBlur,
  error,
  value,
}: OtpProps) {
  let otpInput = useRef(Array(numInputs).fill({}));

  const [state, dispatch] = useReducer(reducer, {
    // cursor used to keep track of sms input
    cursor: 0,
    values: Array(numInputs).fill(''),
  });

  // This is a bit of a hack to reset the field
  // if value prop is set to empty string
  useEffect(() => {
    if (value === '') {
      otpInput.current?.forEach(e => {
        e.clear();
      });
      dispatch({type: 'reset'});
    }
  }, [value]);

  return (
    <View style={[styles.containerStyle, containerStyle]}>
      {state.values.map((val, i) => {
        return (
          <View key={i} style={{flexGrow: 1, flexShrink: 1}}>
            <TextInput
              autoCapitalize="none"
              keyboardType="numeric"
              largeText
              selectTextOnFocus
              ref={e => (otpInput.current[i] = e)}
              value={val}
              placeholder={'•'}
              pattern="[0-9]"
              onTouchStart={e => {
                onTouchStart?.(e);
              }}
              onBlur={e => {
                onBlur?.(e);
              }}
              onChange={e => {
                console.log('onChange', e);
                e.preventDefault();
                if (e.nativeEvent.inputType === 'deleteContentBackward') {
                  dispatch({
                    type: 'deleteContentBackward',
                    payload: {index: i, onChangeText},
                  });
                  if (i > 0) {
                    otpInput.current[i - 1].focus();
                  }
                } else if (
                  // web
                  e.nativeEvent.inputType === 'insertText' ||
                  // native
                  Platform.select({native: true})
                ) {
                  // from sms on chrome ios has inputType insertText
                  // also check that data and text match and have 6 chars
                  // to be fairly certain its from sms
                  if (
                    (e.nativeEvent.data === e.nativeEvent.text &&
                      e.nativeEvent.data.length === 6) ||
                    // native
                    (Platform.select({native: true}) &&
                      e.nativeEvent.text.length === 6)
                  ) {
                    dispatch({
                      type: 'insertFromPaste',
                      payload: {value: e.nativeEvent.text, onChangeText},
                    });

                    // move focus to the last input and then blur
                    otpInput.current[5].focus();
                    otpInput.current[5].blur();
                  } else {
                    // this is when entering numbers normally web or native
                    dispatch({
                      type: 'insertText',
                      payload: {
                        index: i,
                        value: e.nativeEvent.data || e.nativeEvent.text,
                        onChangeText,
                      },
                    });
                    if (i < numInputs - 1) {
                      otpInput.current[i + 1].focus();
                    }
                  }
                } else if (e.nativeEvent.inputType === 'insertFromPaste') {
                  dispatch({
                    type: 'insertFromPaste',
                    payload: {value: e.nativeEvent.text, onChangeText},
                  });
                }
                // Paste code from SMS ios safari
                // code from sms is sent as several
                // onChange event nativeEvent.type = 'input'
                else if (e.nativeEvent.type === 'input') {
                  dispatch({
                    type: 'sms',
                    payload: {value: e.nativeEvent.text, onChangeText},
                  });
                }
              }}
              onKeyPress={e => {
                if (e.nativeEvent.key === 'Backspace') {
                  if (val.length === 0) {
                    if (i > 0) {
                      e.preventDefault();
                      otpInput.current[i - 1].focus();
                    }
                  }
                } else if (e.nativeEvent.key === 'Enter') {
                  onEnter?.();
                }
              }}
              style={[
                styles.textInputStyle,
                error && styles.textInputStyleError,
              ]}
              errorStyle={styles.errorStyle}
            />
          </View>
        );
      })}
    </View>
  );
}
